import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Badge, Box, Button, Modal, Stack, Title } from '@mantine/core';
import Callout from '@ui/components/Callout/Callout';
import Text from '@ui/components/Text/Text';

import useDelayedOpening from 'src/new_arch/hooks/useDelayedOpening';
import Loader from '../../../../../components/Loader/Loader';
import PriceLine from '../../../../../components/subscription/PriceLine/PriceLine';
import AddOnsSwitch from '../../../../../components/AddOnsSwitch/AddOnsSwitch';

import './AddOnsModal.scss';
import { PRICING_PLANS_AVAILABLE } from '../../../../../config/global';
import AddOnModalPresenter from './presenter/AddOnsModalPresenter';

export type AddOnsModalProps = {
	presenter: AddOnModalPresenter
};

const MODAL_STYLES = {
	header: {
		backgroundColor: 'var(--color-bg-container)',
		padding: '18px 28px'
	},
	title: {
		color: 'var(--color-text-primary)',
		fontSize: '14px',
		fontWeight: 600,
		lineHeight: '22px'
	},
	body: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		padding: '0 28px 28px 28px',
		backgroundColor: 'var(--color-bg-container)'
	},
	content: {
		backgroundColor: 'var(--color-bg-container)'
	}
} as const;

export const AddOnsModal = ( {
	presenter
}: AddOnsModalProps ) => {
	const opened = useDelayedOpening();
	useEffect( presenter.onMount, [] );

	return PRICING_PLANS_AVAILABLE && (
		<Modal
			opened={opened}
			onClose={presenter.close}
			title="Customize your plan"
			styles={MODAL_STYLES}
			size="420px"
		>
			<Box sx={{ width: '100%', height: 0, outline: '1px solid var(--color-outline-light)', outlineOffset: '-0.5px' }} />
			<div className="AddOnsModal">
				{presenter.inStudioTrial && presenter.isSeatsSelected && (
					<Callout
						type="promotional"
						variant="outlined"
						text="Seats will keep access during your free trial and become paid when you upgrade."
						mt={8}
						mb={16}
					/>
				)}
				{!presenter.inStudioTrial && (
					<AddOnsSwitch
						onSwitch={presenter.addOnTypeWasSwitched}
						selectedAddOnType={presenter.selectedAddOnType}
					/>
				)}
				<Loader active={presenter.loading} />
				{!presenter.loading && (
					<Stack className="AddOnsModalContent" spacing="14px">
						<Stack spacing="14px">
							<Title order={3}>{presenter.title}</Title>
							<Text color="var(--color-text-light)" variant="body">{presenter.text}</Text>
							<PriceLine
								cost={presenter.cost}
								costDescription={presenter.costDescription}
								tooltipInfoText={presenter.infoText}
							/>
						</Stack>
						<Stack spacing="24px">
							{presenter.hasAddOns && (
								<Badge variant="filled" w="fit-content" sx={{ backgroundColor: 'var(--color-label-yellow)', color: 'var(--color-text-primary)', padding: '0 8px', borderRadius: 4, height: 28 }}>
									<Text variant="body-bold">{presenter.quantityText}</Text>
								</Badge>
							)}
							{presenter.additionalInformation && (
								<Callout
									type="promotional"
									text={presenter.additionalInformation}
								/>
							)}
							<Button
								onClick={presenter.buyButtonWasClicked}
								fullWidth
								disabled={!presenter.actionsEnabled}
								size="sm"
								h={36}
							>
								{presenter.buttonText}
							</Button>
							{presenter.hasAddOns && (
								<Button
									h={20}
									compact
									variant="transparent"
									sx={{ 'color': 'var(--color-button-danger)', '&:hover': { textDecoration: 'underline' } }}
									onClick={presenter.removeButtonWasClicked}
									fullWidth
									disabled={!presenter.actionsEnabled}
								>
									{presenter.removeButtonText}
								</Button>
							)}
						</Stack>
					</Stack>
				)}
			</div>
		</Modal>
	);
};

export default observer( AddOnsModal as React.FC<AddOnsModalProps> );
